import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Alert, Spin, Switch, message, Select, Upload } from 'antd';
import { RobotOutlined, FileImageOutlined, FileWordOutlined, FileExcelOutlined, FilePdfOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../WorkflowDesigner/services/apiService';

const { TextArea } = Input;

const DOC_ACCEPT = '.doc,.docx,.xls,.xlsx,.pdf';
const DOC_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.pdf'];

const EFormDesignerAI = ({ 
  visible, 
  onClose, 
  onSuccess,
  htmlContent,
  isGenerating,
  setIsGenerating 
}) => {
  const { t } = useLanguage();
  const [aiPrompt, setAiPrompt] = useState('');
  const [includeCurrentHtml, setIncludeCurrentHtml] = useState(() => {
    const initialContent = htmlContent || '';
    return initialContent.trim().length > 0;
  });
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);
  const [sendDefaultSystemPrompt, setSendDefaultSystemPrompt] = useState(true);
  const [modifySystemPrompt, setModifySystemPrompt] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  useEffect(() => {
    if (visible) {
      loadAiProviders();
      // 僅在「第一次等 prompt」時預設為 on；已有表單內容（修改中）時預設為 off，避免設計走樣
      const hasExistingForm = (htmlContent || '').trim().length > 0;
      setSendDefaultSystemPrompt(!hasExistingForm);
    }
  }, [visible, htmlContent]);

  const loadAiProviders = async () => {
    try {
      setLoadingProviders(true);
      const providers = await apiService.fetchAiProviders();
      setAiProviders(providers || []);
      setSelectedProviderKey(prev => {
        if (prev) {
          return prev;
        }
        if (providers && providers.length > 0) {
          const active = providers.find(p => p.active);
          return (active || providers[0]).providerKey;
        }
        return '';
      });
    } catch (error) {
      console.error('Failed to load AI providers for EForm designer', error);
      message.error(t('eformDesigner.loadAiProvidersFailed'));
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleImageUpload = (file) => {
    // 檢查文件類型
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('請上傳圖片文件');
      return false;
    }

    // 檢查文件大小（限制 10MB）
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('圖片大小不能超過 10MB');
      return false;
    }

    // 創建預覽
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setUploadedImage(file);
    return false; // 阻止自動上傳
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  const handleDocumentUpload = (file) => {
    const ext = (file.name || '').toLowerCase().replace(/.*\./, '.');
    if (!DOC_EXTENSIONS.includes(ext)) {
      message.error(t('eformDesigner.uploadWordExcelPdfOnly') || '僅支援 Word (.doc/.docx)、Excel (.xls/.xlsx)、PDF (.pdf)');
      return false;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error(t('eformDesigner.documentSizeLimit') || '文件大小不能超過 50MB');
      return false;
    }
    setUploadedDocument(file);
    return false;
  };

  const handleRemoveDocument = () => {
    setUploadedDocument(null);
  };

  const loadDefaultSystemPrompt = async () => {
    if (customSystemPrompt.trim()) return;
    setLoadingDefaultPrompt(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/FormsUpload/ai-default-system-prompt', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      if (data.defaultSystemPrompt != null) setCustomSystemPrompt(data.defaultSystemPrompt);
    } catch (e) {
      message.error(t('eformDesigner.loadDefaultPromptFailed') || '無法載入預設系統 prompt');
    } finally {
      setLoadingDefaultPrompt(false);
    }
  };

  const handleModifySystemPromptChange = (checked) => {
    setModifySystemPrompt(checked);
    if (checked) loadDefaultSystemPrompt();
    else setCustomSystemPrompt('');
  };

  const handleSendDefaultSystemPromptChange = (checked) => {
    setSendDefaultSystemPrompt(checked);
    if (!checked) {
      setModifySystemPrompt(false);
      setCustomSystemPrompt('');
    }
  };

  const handleAiGenerateForm = async () => {
    const hasPrompt = !!aiPrompt.trim();
    const hasImage = !!uploadedImage;
    const hasDocument = !!uploadedDocument;
    if (!hasPrompt && !hasImage && !hasDocument) {
      message.warning(t('eformDesigner.pleaseEnterYourRequirements') || '請輸入需求描述、上傳表單圖片或 Word/Excel/PDF 文件');
      return;
    }

    if (aiProviders.length > 0 && !selectedProviderKey) {
      message.warning(t('eformDesigner.aiProviderRequired'));
      return;
    }

    setIsGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000); // 15 分鐘

      let response;

      if (uploadedImage || uploadedDocument) {
        const formData = new FormData();
        if (uploadedImage) {
          formData.append('file', uploadedImage);
        }
        if (uploadedDocument) {
          formData.append('documentFile', uploadedDocument);
        }
        formData.append('prompt', aiPrompt.trim() || '');
        formData.append('includeCurrentHtml', includeCurrentHtml.toString());
        formData.append('providerKey', selectedProviderKey || '');
        if (includeCurrentHtml && (htmlContent || '').trim()) {
          formData.append('currentHtml', (htmlContent || '').trim());
        }
        formData.append('useDefaultSystemPrompt', sendDefaultSystemPrompt.toString());
        if (sendDefaultSystemPrompt && modifySystemPrompt && customSystemPrompt.trim()) {
          formData.append('customSystemPrompt', customSystemPrompt.trim());
        }
        // patch 模式：有現有 HTML 時自動使用 operations 格式
        formData.append('returnFormat', (includeCurrentHtml && (htmlContent || '').trim()) ? 'operations' : 'html');
        response = await fetch('/api/FormsUpload/ai-generate-with-image', {
          method: 'POST',
          headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          body: formData,
          signal: controller.signal
        });
      } else {
        const requestData = {
          prompt: aiPrompt.trim(),
          includeCurrentHtml: includeCurrentHtml,
          providerKey: selectedProviderKey || null,
          // patch 模式：有現有 HTML 時自動使用 operations 格式
          ReturnFormat: (includeCurrentHtml && (htmlContent || '').trim()) ? 'operations' : 'html'
        };
        if (includeCurrentHtml && (htmlContent || '').trim()) {
          requestData.CurrentHtml = (htmlContent || '').trim();
        }
        requestData.UseDefaultSystemPrompt = sendDefaultSystemPrompt;
        if (sendDefaultSystemPrompt && modifySystemPrompt && customSystemPrompt.trim()) {
          requestData.CustomSystemPrompt = customSystemPrompt.trim();
        }
        response = await fetch('/api/FormsUpload/ai-generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify(requestData),
          signal: controller.signal
        });
      }

      clearTimeout(timeoutId);
      const result = await response.json();

      if (result.success) {
        const finalHtml = result.htmlContent;

        // 安全驗證：確保 finalHtml 是合法 HTML（防止 AI 異常回應破壞表單）
        const isValidHtml = finalHtml && (
          finalHtml.includes('<html') || finalHtml.includes('<!DOCTYPE') ||
          finalHtml.includes('<body') || finalHtml.includes('<table') || finalHtml.includes('<form')
        );
        if (!isValidHtml) {
          message.error(`❌ ${t('eformDesigner.generationFailed')}AI 返回了無效格式，表單未更新`);
          return;
        }

        if (result.mode === 'patch') {
          console.log(`[EFormDesignerAI] patch 模式：套用 ${result.appliedCount ?? '?'} 個，跳過 ${result.skippedCount ?? 0} 個 (後端處理)`);
        }

        message.success(`✅ ${t('eformDesigner.aiFormGeneratedSuccess')}`);
        onSuccess(finalHtml, result.formName || t('eformDesigner.aiGeneratedForm'));
        setAiPrompt('');
        setUploadedImage(null);
        setImagePreview(null);
        setUploadedDocument(null);
        onClose();
      } else {
        message.error(`❌ ${t('eformDesigner.generationFailed')}${result.error || t('eformDesigner.unknownError')}`);
      }
    } catch (error) {
      console.error('❌ AI 生成錯誤:', error);
      if (error.name === 'AbortError') {
        message.error(`❌ ${t('eformDesigner.requestTimeoutAiGeneration')}`);
      } else {
        message.error(`❌ ${t('eformDesigner.generationFailed')}${error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <RobotOutlined style={{ color: '#1890ff' }} />
          {t('eformDesigner.aiGenerateForm')}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('eformDesigner.cancel')}
        </Button>,
        <Button
          key="generate"
          type="primary"
          onClick={handleAiGenerateForm}
          loading={isGenerating}
          disabled={
            (!aiPrompt.trim() && !uploadedImage && !uploadedDocument) ||
            (aiProviders.length > 0 && !selectedProviderKey)
          }
        >
          {isGenerating ? t('eformDesigner.generating') : t('eformDesigner.generateForm')}
        </Button>
      ]}
      width={600}
    >
      <div style={{ padding: '20px 0' }}>
        <Alert
          message={`🤖 ${t('eformDesigner.aiSmartGeneration')}`}
          description={t('eformDesigner.describeTheFormTypeAndRequirements')}
          type="info"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('eformDesigner.uploadFormImage') || '上傳表單圖片（可選）'}
          </label>
          {!uploadedImage ? (
            <Upload.Dragger
              name="file"
              accept="image/*"
              beforeUpload={handleImageUpload}
              showUploadList={false}
              disabled={isGenerating}
            >
              <p className="ant-upload-drag-icon">
                <FileImageOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">
                {t('eformDesigner.clickOrDragImageHere') || '點擊或拖拽圖片到此區域上傳'}
              </p>
              <p className="ant-upload-hint">
                {t('eformDesigner.supportsJpgPngGifBmpWebpFormats') || '支持 JPG、PNG、GIF、BMP、WEBP 格式'}
              </p>
            </Upload.Dragger>
          ) : (
            <div style={{ position: 'relative', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px' }}>
              <img 
                src={imagePreview} 
                alt="預覽" 
                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemoveImage}
                style={{ position: 'absolute', top: '8px', right: '8px' }}
                disabled={isGenerating}
              >
                移除
              </Button>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('eformDesigner.uploadFormDocumentOptional') || '上傳 Word/Excel/PDF（可選）'}
          </label>
          {!uploadedDocument ? (
            <Upload.Dragger
              name="documentFile"
              accept={DOC_ACCEPT}
              beforeUpload={handleDocumentUpload}
              showUploadList={false}
              disabled={isGenerating}
            >
              <p className="ant-upload-drag-icon">
                <span style={{ fontSize: '32px' }}>
                  <FileWordOutlined style={{ color: '#2b579a', marginRight: 4 }} />
                  <FileExcelOutlined style={{ color: '#217346', marginRight: 4 }} />
                  <FilePdfOutlined style={{ color: '#e74c3c' }} />
                </span>
              </p>
              <p className="ant-upload-text">
                {t('eformDesigner.clickOrDragDocHere') || '點擊或拖拽 Word/Excel/PDF 到此處'}
              </p>
              <p className="ant-upload-hint">
                {t('eformDesigner.documentConvertedToHtmlForAi') || '將以 LibreOffice 轉為 HTML 供 AI 辨識欄位，與圖片一併分析可得到更準確表單'}
              </p>
            </Upload.Dragger>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px 12px' }}>
              <span style={{ fontWeight: 500 }}>{uploadedDocument.name}</span>
              <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={handleRemoveDocument} disabled={isGenerating}>
                {t('eformDesigner.remove') || '移除'}
              </Button>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('eformDesigner.requirementsDescription')}
          </label>
          <TextArea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={uploadedImage ? (t('eformDesigner.placeholderWithImage') || '（可選）描述額外需求，或留空讓 AI 自動分析圖片') : (t('eformDesigner.placeholderExample') || '例如：創建一個包含姓名、電話、地址的表單')}
            rows={6}
            style={{ fontSize: '14px' }}
            disabled={isGenerating}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('eformDesigner.aiProvider')}
          </label>
          <Select
            value={selectedProviderKey || undefined}
            onChange={value => setSelectedProviderKey(value)}
            placeholder={t('eformDesigner.aiProviderPlaceholder')}
            style={{ width: '100%' }}
            loading={loadingProviders}
            allowClear
            options={aiProviders.map(provider => ({
              value: provider.providerKey,
              label: `${provider.displayName}${provider.active ? '' : ` (${t('workflowDesigner.aiProviderInactive')})`}`,
              disabled: provider.active === false
            }))}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <Switch
              checked={sendDefaultSystemPrompt}
              onChange={handleSendDefaultSystemPromptChange}
              style={{ marginRight: '8px' }}
              disabled={isGenerating}
            />
            <span style={{ fontWeight: 'bold' }}>{t('eformDesigner.sendDefaultSystemPrompt')}</span>
          </label>
          <div style={{ marginBottom: '16px', marginTop: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', opacity: sendDefaultSystemPrompt ? 1 : 0.6 }}>
              <input
                type="checkbox"
                checked={modifySystemPrompt}
                onChange={(e) => handleModifySystemPromptChange(e.target.checked)}
                style={{ marginRight: '8px' }}
                disabled={isGenerating || !sendDefaultSystemPrompt}
              />
              <span style={{ fontWeight: 'bold' }}>{t('eformDesigner.modifySystemPrompt')}</span>
            </label>
            {sendDefaultSystemPrompt && modifySystemPrompt && (
              <div style={{ marginTop: '8px' }}>
                <TextArea
                  value={customSystemPrompt}
                  onChange={(e) => setCustomSystemPrompt(e.target.value)}
                  placeholder={loadingDefaultPrompt ? (t('eformDesigner.loadingDefaultPrompt') || '載入預設中...') : (t('eformDesigner.systemPromptPlaceholder') || '系統預設 prompt（可編輯）')}
                  rows={8}
                  style={{ fontSize: '13px', fontFamily: 'monospace' }}
                  disabled={isGenerating || loadingDefaultPrompt}
                />
              </div>
            )}
          </div>
        </div>

        {htmlContent.trim() && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              {t('eformDesigner.includeCurrentHtml')}：
            </label>
            <Switch
              checked={includeCurrentHtml}
              onChange={(checked) => setIncludeCurrentHtml(checked)}
              style={{ marginBottom: '16px' }}
            />
            <Alert
              message={includeCurrentHtml ? `✅ ${t('eformDesigner.willModifyBasedOnCurrentContent')}` : `🔄 ${t('eformDesigner.willGenerateNewForm')}`}
              description={
                includeCurrentHtml 
                  ? t('eformDesigner.aiWillModifyAndOptimizeBasedOnYourCurrentFormContent').replace('{length}', htmlContent.length)
                  : t('eformDesigner.aiWillGenerateANewFormBasedOnYourDescription')
              }
              type={includeCurrentHtml ? "success" : "info"}
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        )}
        
        {isGenerating && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '8px', color: '#666' }}>
              {t('eformDesigner.aiGeneratingForm')}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EFormDesignerAI; 